import { createBrowserRouter, Navigate } from 'react-router-dom';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import RequireAuth from '@/auth/RequireAuth';
import { lazyWithReload } from '@/lib/lazyWithReload';
import SignIn from '@/pages/SignIn';
import Overview from '@/pages/Overview';
import FraudHub from '@/pages/FraudHub';
import FraudDashboard from '@/pages/FraudDashboard';
// These routes are lazy, so they are the ones exposed to a stale module graph
// after a rebuild — hence lazyWithReload rather than a bare React.lazy.
// FraudCenter pulls in MUI X Data Grid Pro — lazy-load it so that heavy chunk
// only downloads when the route is visited (see manualChunks in vite.config).
const FraudCenter = lazyWithReload(() => import('@/pages/FraudCenter'));
// Offender Vehicles pulls in the same MUI X Data Grid Pro chunk — lazy-load it
// so that heavy chunk only downloads when a grid route is actually visited.
const OffenderVehicles = lazyWithReload(() => import('@/pages/OffenderVehicles'));
// Lazy like the other Intelligence pages so its page code stays out of the
// initial bundle. (MapLibre itself is already in the main chunk via the eager
// Maps route, so this defers the page, not the map engine.)
const FraudMap = lazyWithReload(() => import('@/pages/FraudMap'));
import AlertDetail from '@/pages/AlertDetail';
import IntelligenceMethods from '@/pages/IntelligenceMethods';
import Consumption from '@/pages/Consumption';
import MerchantNetwork from '@/pages/MerchantNetwork';
import Maps from '@/pages/Maps';
import MerchantOps from '@/pages/MerchantOps';
import FuelMerchants from '@/pages/FuelMerchants';
import Departments from '@/pages/Departments';
import Maintenance from '@/pages/Maintenance';
import Utilisation from '@/pages/Utilisation';
import Forecasting from '@/pages/Forecasting';
import Emissions from '@/pages/Emissions';
import Telematics from '@/pages/Telematics';
import DataQuality from '@/pages/DataQuality';
import RuleHealth from '@/pages/RuleHealth';
import FleetHealth from '@/pages/FleetHealth';
import VehicleDetail from '@/pages/VehicleDetail';
import VehicleSearch from '@/pages/VehicleSearch';

// Caddy mounts the SPA at /app/, so all client routes live under that
// basename. Vite's `base: '/app/'` ensures asset paths agree.
export const router = createBrowserRouter(
  [
    { path: '/sign-in', element: <SignIn /> },
    {
      path: '/',
      element: (
        <RequireAuth>
          <DashboardLayout />
        </RequireAuth>
      ),
      children: [
        { index: true, element: <Navigate to="/overview" replace /> },
        { path: 'overview', element: <Overview /> },
        { path: 'offenders', element: <OffenderVehicles /> },
        { path: 'fraud-map', element: <FraudMap /> },
        { path: 'methodology', element: <IntelligenceMethods /> },
        { path: 'fraud-hub', element: <FraudHub /> },
        { path: 'fraud-dashboard', element: <FraudDashboard /> },
        { path: 'fraud', element: <FraudCenter /> },
        { path: 'fraud/:alertId', element: <AlertDetail /> },
        { path: 'consumption', element: <Consumption /> },
        { path: 'merchant-network', element: <MerchantNetwork /> },
        { path: 'maps', element: <Maps /> },
        { path: 'merchant-ops', element: <MerchantOps /> },
        { path: 'fuel-merchants', element: <FuelMerchants /> },
        { path: 'departments', element: <Departments /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: 'utilisation', element: <Utilisation /> },
        { path: 'forecasting', element: <Forecasting /> },
        { path: 'emissions', element: <Emissions /> },
        { path: 'telematics', element: <Telematics /> },
        { path: 'data-quality', element: <DataQuality /> },
        { path: 'rule-health', element: <RuleHealth /> },
        { path: 'fleet-health', element: <FleetHealth /> },
        { path: 'vehicle-search', element: <VehicleSearch /> },
        { path: 'vehicle/:assetToken', element: <VehicleDetail /> },
        { path: '*', element: <Navigate to="/overview" replace /> },
      ],
    },
  ],
  { basename: '/app' },
);
